import { Vertical } from '@/types/verticals'
import {
  InMemoryCache,
  createHttpLink,
  ApolloClient,
  from,
  type NormalizedCacheObject,
  ApolloLink,
} from '@apollo/client/core'
import { processUrl, getConsistencyToken, setConsistencyToken } from '@/lib/net'
import { setContext } from '@apollo/client/link/context'
import { VERSION } from '@/lib/defines'
import { getAccessToken, hasValidUserSession } from '@/services/identity-client'
import { getWeekOfYear } from '@/lib/date'

import possibleTypes from '@/assets/possible-types.json'

let _apolloClient: ApolloClient<NormalizedCacheObject> | undefined

/**
 * Store the app the client is created for. If the client is requested for a new
 * app vertical, we need to make a new one.
 */
let _apolloClientApp: Vertical | undefined

/**
 * *(AI-generated)*
 * Builds and returns the standard ONX request headers including authorization and consistency tokens.
 */
export const getOnxHeaders = async (appName: Vertical) => {
  let authorization = {}
  let consistencyHeader = {}
  // Only include auth headers if user is logged in
  if (await hasValidUserSession()) {
    authorization = { Authorization: `Bearer ${await getAccessToken()}` }
  }

  const consistencyToken = getConsistencyToken()
  if (consistencyToken) {
    consistencyHeader = { 'Onx-Consistency-Token': consistencyToken }
  }

  return {
    'ONX-Application-ID': appName,
    'ONX-Application-Platform': 'web',
    'ONX-Application-Version': VERSION,
    ...authorization,
    ...consistencyHeader,
  }
}

/**
 * *(AI-generated)*
 * Builds and returns the current build version string.
 */
export const makeBuildVersion = () => {
  // Most of this information will be blank while developing locally
  const sha = process.env.SHORT_SHA ? `+${process.env.SHORT_SHA}` : ''
  const release = process.env.GITHUB_RUN_NUMBER
    ? `-${process.env.GITHUB_RUN_NUMBER}`
    : ''

  // Use the release date if possible (on prod)
  const dateStr = process.env.SENTRY_RELEASE_DATE
  const releaseDate =
    dateStr === '' || dateStr === undefined ? new Date() : new Date(dateStr)
  const year = releaseDate.getFullYear()
  const yearShort = year.toString().slice(2)
  const week = getWeekOfYear(releaseDate)
  // Example return should look like `23.2-1234+abc123`
  return `${yearShort}.${week}${release}${sha}`
}

/**
 * Creates an apollo client
 *
 * @param appName used to identify request source
 */
export const createApolloClient = (appName: Vertical) => {
  const uri = processUrl('/v1/supergraph/')
  const env = process.env.NODE_ENV === 'development' ? '.dev' : ''
  const buildVersion = makeBuildVersion()
  const httpLink = createHttpLink({
    uri,
  })
  // Get new auth header for every apollo request since they expire
  const authLink = setContext(async (_, { headers }) => {
    return {
      headers: {
        ...headers,
        ...(await getOnxHeaders(appName)),
      },
    }
  })
  // Look for the consistency token in the response headers
  const consistencyTokenResponseLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext()
      const {
        response: { headers },
      } = context

      if (headers) {
        const token = headers.get('Onx-Consistency-Token')
        if (token) {
          setConsistencyToken(token)
        }
      }

      return response
    })
  })

  const client = new ApolloClient({
    cache: new InMemoryCache({ possibleTypes }),
    link: from([authLink, consistencyTokenResponseLink, httpLink]),
    name: `onxmaps.${appName}.web${env}`,
    version: `${buildVersion}`,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
  })

  return client
}

/** Creates one Apollo client per vertical, keyed by vertical name, for cross-vertical queries. */
export const createAllVerticalClients = (): Record<
  Vertical,
  ApolloClient<NormalizedCacheObject>
> => ({
  [Vertical.Hunt]: createApolloClient(Vertical.Hunt),
  [Vertical.Offroad]: createApolloClient(Vertical.Offroad),
  [Vertical.Backcountry]: createApolloClient(Vertical.Backcountry),
  [Vertical.Fish]: createApolloClient(Vertical.Fish),
})

/**
 * Returns apollo client, creates if needed.
 */
export const getApolloClient = () => {
  if (_apolloClient && Vertical.Fish === _apolloClientApp) {
    return _apolloClient
  }
  const client = createApolloClient(Vertical.Fish)
  _apolloClientApp = Vertical.Fish
  _apolloClient = client
  return client
}
